import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { PreTask } from "../../interfaces/Task";
import { Media } from "../../interfaces/Media";
import Uploader from "../../elements/Uploader";
import CustomEditor from "../../elements/CustomEditor";
import { registerEventEnd, registerEventStart } from "../../../utils/utils";
import { useNavigate } from "react-router-dom";
import AITools from "../../elements/AITools";
import CloneAs from "../../elements/CloneAs";
import { useTranslation } from "react-i18next";
import { Overlay } from "../../elements/Overlay";

const emptyPreTask: any = {
    _id: '',
    name: '',
    type: 'game',
    instructions: '',
    media: [],
    text: '',
    updated: new Date()
}

export interface EditPreTaskGameProps {
    task_id: string;
    id: string;
    position: number;
    saveForm: CallableFunction;
    closeForm: CallableFunction;
    cloneAs: CallableFunction;
    task_language?: string;
}

export default function EditPreTaskGame(props: EditPreTaskGameProps) {
    const { t } = useTranslation();
    
    const navigate = useNavigate();
    const [eventId, setEventId] = useState('');
    const [preTask, setPreTask] = useState<PreTask>(emptyPreTask);
    const [name, setName] = useState('');
    const [instructions, setInstructions] = useState('');
    const [teacher_notes, setTeacherNotes] = useState('');
    const [text, setText] = useState('');
    const [media, setMedia] = useState<Media[]>([]);
    const [formattedText, setFormattedText] = useState('');

    const handleMediaUpload = (media: Media[]) => {
        const newMedia = media.map((m: Media) => {
            return m;
        });
        setMedia(newMedia);
    }

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    }

    const handleInstructionsChange = (data: string) => {
        setInstructions(data);
    }

    const handleTeacherNotesChange = (data: string) => {
        setTeacherNotes(data);
    }

    const handleTextChange = (data: string) => {
        setText(data);
    }

    const handleClose = () => {
        registerEventEnd(eventId);
        props.closeForm();
    }

    const handleSave = () => {
        if (name) {
            registerEventEnd(eventId);
            preTask.name = name;
            preTask.instructions = instructions;
            preTask.media = media.map((m: Media) => { return m; });
            preTask.text = text;
            preTask.formatted_text = formattedText;
            preTask.teacher_notes = teacher_notes;
            props.saveForm(preTask);
        }
    }

    useEffect(() => {
        const requestOptionsPost = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            }
        };
        fetch(import.meta.env.VITE_API_URL + '/v1/task/' + props.task_id + '/pretask/' + props.id, requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {
                const newMedia: Media[] = data.media.map((m: Media) => { return m; });
                setMedia(newMedia);
                setPreTask(data);
                setName(data.name);
                setFormattedText(data.formatted_text);
            });

        registerEventStart('edit pretask game', props.task_id).then(data => { 
            setEventId(data);
        });

        return () => {
            registerEventEnd(eventId);
        };
    }, []);

    const handleCloneAs = (id: string, direction: string) => {
        props.cloneAs(id, direction);
    }

    const handleSaveFormattedText = (text: string) => {
        setFormattedText(text);
    }

    const doNothing = () => {
        
    }

    return(
        <Overlay
            title={ t("Edit Pre-Task: Game") }
            onClose={ handleClose }
            actions={[
                {
                    label: t("Cancel"),
                    variant: 'secondary',
                    onClick: handleClose
                },
                {
                    label: t("Save"),
                    variant: 'primary',
                    onClick: handleSave
                }
            ]}
        >
            <div className="inner">
                <AITools />
                <Form className="new-pre-task-form">
                    <div className="fields-container">
                        <Form.Group className="field field-title">
                            <Form.Label>{ t("Title of the Pre-Task") }</Form.Label>
                            <Form.Control type="text" name="name" required value={ name } onChange={ handleNameChange } />
                        </Form.Group>
                        <div className="field field-instructions">
                            <CustomEditor
                                change={ handleInstructionsChange }
                                changeFormatted={ doNothing }
                                data={ preTask.instructions }
                                formatted= { '' }
                                id="instructions"
                                label={ t("Aim and instructions") }
                                npl={ false }
                                task_id={ '' }
                            />
                        </div>
                        <div className="field field-teachernotes">
                            <CustomEditor
                                change={ handleTeacherNotesChange }
                                changeFormatted={ doNothing }
                                data={ preTask.teacher_notes }
                                formatted= { '' }
                                id="teachernotes"
                                label={ t("Teacher notes") }
                                npl={ false }
                                task_id={ '' }
                            />
                        </div>
                        <Uploader media={ media } save={ handleMediaUpload } />
                        <div className="field field-text">
                            <CustomEditor
                                change={ handleTextChange }
                                changeFormatted={ handleSaveFormattedText }
                                data={ preTask.text }
                                formatted={ preTask.formatted_text }
                                id="text"
                                label={ t("Text")}
                                npl={ true }
                                task_id={ props.task_id }
                                task_language={ props.task_language }
                            />
                        </div>
                        <CloneAs id={ props.id } direction={ 'pre-to-pos' } cloneAs={ handleCloneAs } />
                    </div>
                </Form>
            </div>
        </Overlay>
    );
}